import { type ComponentType, useState, type ReactNode } from 'react';

import Divider from '@liquidweb-dev/undercurrent/Divider';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import CalendarIcon from '@liquidweb-dev/undercurrent/icons/lucide/Calendar';
import CalendarClockIcon from '@liquidweb-dev/undercurrent/icons/lucide/CalendarClock';
import EllipsisIcon from '@liquidweb-dev/undercurrent/icons/lucide/Ellipsis';
import EyeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Eye';
import HeadsetIcon from '@liquidweb-dev/undercurrent/icons/lucide/Headset';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import ScanBarcodeIcon from '@liquidweb-dev/undercurrent/icons/lucide/ScanBarcode';
import Trash2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import Link from '@liquidweb-dev/undercurrent/Link';
import ListItem from '@liquidweb-dev/undercurrent/ListItem';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import type { LWApiPublicSecureDataShareDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/secureDataShare/index.raw';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Link as RouterLink } from 'react-router-dom';
import { useDialogSecureNotesContext } from './context';
import PopoverDeleteNote from './PopoverDeleteNote';
import PopoverSupportCase from './PopoverSupportCase';
import SecureNote from './SecureNote';

dayjs.extend(localizedFormat);

interface KeyItemProps {
	Icon: ComponentType<LucideSvgProps>;
	label: string;
	value: ReactNode;
}

const KeyItem = ({ Icon, label, value }: KeyItemProps) => (
	<Stack sx={{ flexDirection: 'row', gap: 100 }}>
		<Icon
			sx={({ size, vars }) => ({
				height: size.xS,
				width: size.xS,
				color: vars.palette.uc.icon.weak,
			})}
		/>

		<Stack sx={{ gap: 50 }}>
			<Typography fontType="monospace" variant="label-lg">
				{value}
			</Typography>
			<Typography fontType="monospace" variant="label-md">
				{label}
			</Typography>
		</Stack>
	</Stack>
);

export type SecureNoteRowProps = LWApiPublicSecureDataShareDetailsResultRawI;

const SecureNoteRow = ({
	added_date: addedDate,
	description,
	expiration,
	id,
	label,
	remaining_views: remainingViews,
	ticketNumbers,
}: SecureNoteRowProps) => {
	const { unlockedId } = useDialogSecureNotesContext();
	const { closeDialog } = useDialogSecureNotesContext();
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [menuKey, setMenuKey] = useState<
		'primaryMenu' | 'editSupportCase' | 'deleteNote' | null
	>(null);

	const supportCases = ticketNumbers?.map(
		({ ticket_number: ticketNumber }) => ticketNumber,
	);
	const hasSupportCase = supportCases && supportCases.length > 0;

	return (
		<ListItem sx={{ flexDirection: 'column', gap: 200, alignItems: 'normal' }}>
			<Stack
				sx={{
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: 200,
					alignItems: 'start',
				}}
			>
				<Stack sx={{ gap: 100 }}>
					<Typography variant="body-md" sx={{ fontWeight: 500 }}>
						{description || label}
					</Typography>

					<Stack sx={{ flexDirection: 'row', gap: 300, flexWrap: 'wrap' }}>
						<KeyItem
							Icon={ScanBarcodeIcon}
							label="Note ID"
							value={id.toString()}
						/>
						<KeyItem
							Icon={EyeIcon}
							label="Views"
							value={
								remainingViews === -1 ? 'Infinite' : `${remainingViews} left`
							}
						/>
						<KeyItem
							Icon={CalendarIcon}
							label="Created"
							value={dayjs(addedDate).format('l')}
						/>
						<KeyItem
							Icon={CalendarClockIcon}
							label="Expiration"
							value={dayjs(expiration).format('l LT')}
						/>
						{hasSupportCase && (
							<KeyItem
								Icon={HeadsetIcon}
								label={
									supportCases.length > 1 ? 'Support cases' : 'Support case'
								}
								value={
									<Stack
										sx={{ gap: 150, flexDirection: 'row', flexWrap: 'wrap' }}
									>
										{supportCases.map((supportCase) => (
											<Link
												component={RouterLink}
												key={supportCase}
												to={`/support/mycases/${supportCase}`}
												onClick={closeDialog}
											>
												<Typography fontType="monospace" variant="label-md">
													{supportCase}
												</Typography>
											</Link>
										))}
									</Stack>
								}
							/>
						)}
					</Stack>
				</Stack>

				<IconButton
					variant="barebones"
					onClick={(event) => {
						setMenuAnchorEl(event.currentTarget);
						setMenuKey('primaryMenu');
					}}
					disabled={Boolean(unlockedId)}
				>
					<EllipsisIcon />
				</IconButton>
			</Stack>

			<SecureNote secureDataShareId={Number(id)} />

			<Menu
				open={Boolean(menuKey === 'primaryMenu' && menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}
				anchorEl={menuAnchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<MenuItem
					onClick={() => {
						setMenuKey('editSupportCase');
					}}
				>
					<ListItemIcon>
						{hasSupportCase ? <PencilIcon /> : <PlusIcon />}
					</ListItemIcon>
					<ListItemText>
						{hasSupportCase ? 'Edit support case' : 'Add support case'}
					</ListItemText>
				</MenuItem>

				<Divider sx={{ mx: -50 }} />

				<MenuItem
					onClick={() => {
						setMenuKey('deleteNote');
					}}
					sx={{
						':hover, :focus-visible': {
							backgroundColor: ({ vars }) => vars.palette.uc.danger.bg.main,
						},
					}}
				>
					<ListItemIcon>
						<Trash2Icon
							sx={{ color: ({ vars }) => vars.palette.uc.danger.icon.main }}
						/>
					</ListItemIcon>
					<ListItemText
						sx={{ color: ({ vars }) => vars.palette.uc.danger.text.main }}
					>
						Delete note
					</ListItemText>
				</MenuItem>
			</Menu>

			<PopoverSupportCase
				datashareId={id}
				ticketNumbers={ticketNumbers || []}
				open={Boolean(menuKey === 'editSupportCase' && menuAnchorEl)}
				anchorEl={menuAnchorEl}
				onClose={() => {
					setMenuKey(null);
					setMenuAnchorEl(null);
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			/>

			<PopoverDeleteNote
				datashareId={id}
				open={Boolean(menuKey === 'deleteNote' && menuAnchorEl)}
				anchorEl={menuAnchorEl}
				onClose={() => {
					setMenuKey(null);
					setMenuAnchorEl(null);
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			/>
		</ListItem>
	);
};

export default SecureNoteRow;
